import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import DashboardLayout from '../shared/layout/DashboardLayout';
import config from '../../config/config';
import { getKLMenuItems } from '../../utils/menuItems';
import { ArrowUp, FileText, MessageSquare, X, Download, Edit, CheckCircle, XCircle, Map, Database, GitMerge, FileBadge, Tag, Palette, Users, ShieldCheck, Globe, Info, AlertTriangle, Hourglass, ClipboardCheck, Edit3 } from "lucide-react";
import ProgressBar from 'react-bootstrap/ProgressBar';
import { toast, ToastContainer } from 'react-toastify';
import '../../styles/RekapDataDetails.css';
import 'react-toastify/dist/ReactToastify.css';
import { useUnsealModal } from '../../hooks/useUnsealModal';
import UnsealProgressModal from '../../components/UnsealProgressModal';
import DataChecklistView from '../../components/DataChecklistView';
import MetadataEditor from '../../components/MetadataEditor/MetadataEditor';

const DataDetailPage = () => {
  const { no_tiket } = useParams();
  const navigate = useNavigate();
  const [dataDetail, setDataDetail] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [catatan, setCatatan] = useState({});
  const [status, setStatus] = useState('');
  const [isChecklistViewOpen, setIsChecklistViewOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [checklistStatus, setChecklistStatus] = useState({});

  const handleChecklistViewOpen = (documentType) => {
    setSelectedDocumentType(documentType);
    setIsChecklistViewOpen(true);
  };

  const handleChecklistViewClose = () => {
    setIsChecklistViewOpen(false);
    setSelectedDocumentType('');
  };

  // Fungsi untuk mengecek apakah ada data checklist
  const checkChecklistData = async (noTiket) => {
    if (!noTiket) return;

    const allDocumentTypes = ['peta', 'struktur_data', 'takel', 'dokumen', 'metadata', 'simbology', 'role', 'kualitas', 'cakupan'];
    const statusMap = {};

    // Initialize all document types
    allDocumentTypes.forEach(docType => {
      statusMap[docType] = false;
    });
    const documentsToCheck = [];

    const token = localStorage.getItem('token');

    try {
      const promises = documentsToCheck.map(async (docType) => {
        try {
          const response = await fetch(`${config.API_URL}/api/checklist/data/${noTiket}/${docType}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.status === 404) {
            return { [docType]: false };
          }

          if (response.ok) {
            const data = await response.json();
            if (data && data.checklistData) {
              const items = data.checklistData.items || {};
              return { [docType]: Object.keys(items).length > 0 };
            }
          }

          return { [docType]: false };
        } catch (error) {
          // Silent handling
          return { [docType]: false };
        }
      });

      const results = await Promise.all(promises);
      results.forEach(result => Object.assign(statusMap, result));
      setChecklistStatus(statusMap);
    } catch (error) {
      // Silent handling
      setChecklistStatus(statusMap);
    }
  };
  const [editMode, setEditMode] = useState(false);
  const [fileba, setFileba] = useState('');
  const [filerekom, setFilerekom] = useState('');
  const [verificationDocs, setVerificationDocs] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileData, setFileData] = useState({
    peta: null, struktur_data: null, takel: null, dokumen: null,
    metadata: null, simbology: null, role: null, kualitas: null,
  });
  const [fileValidationErrors, setFileValidationErrors] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  
  // State untuk checklist
  const [checklistData, setChecklistData] = useState([]);
  const [checklistInputs, setChecklistInputs] = useState({});
  const [expandedChecklists, setExpandedChecklists] = useState({});
  
  // State untuk document synchronization
  const [latestDocuments, setLatestDocuments] = useState({});
  const [syncedDocuments, setSyncedDocuments] = useState({});
  const [fileNames, setFileNames] = useState({});
  
  // Capaian states
  const [capaianData, setCapaianData] = useState([]);
  const [kepkaInfo, setKepkaInfo] = useState(null);
  const [futureYearsTarget, setFutureYearsTarget] = useState([]);
  
  // Metadata editor states
  const [showMetadataEditor, setShowMetadataEditor] = useState(false);
  const [metadataContent, setMetadataContent] = useState(null);
  const [metadataUrl, setMetadataUrl] = useState('');

  // Array dokumen untuk dirender di tabel
  const documentItems = [
    { label: 'Peta', key: 'peta', icon: <Map className="document-lucide-icon" /> },
    { label: 'Kamus Data / Struktur Data', key: 'struktur_data', icon: <Database className="document-lucide-icon" /> },
    { label: 'Mekanisme dan Tata Kerja', key: 'takel', icon: <GitMerge className="document-lucide-icon" /> },
    { label: 'Dokumen Penetapan', key: 'dokumen', icon: <FileBadge className="document-lucide-icon" /> },
    { label: 'Metadata', key: 'metadata', icon: <Tag className="document-lucide-icon" /> },
    { label: 'Simbology', key: 'simbology', icon: <Palette className="document-lucide-icon" /> },
    { label: 'Role Akses', key: 'role', icon: <Users className="document-lucide-icon" /> },
    { label: 'Kontrol Kualitas', key: 'kualitas', icon: <ShieldCheck className="document-lucide-icon" /> },
  ];

  const {
    isUnsealModalOpen,
    selectedTicket,
    openUnsealModal,
    closeUnsealModal,
    handleUnsealComplete
  } = useUnsealModal();

  const allowedFormats = {
    peta: ['.gdb', '.shp', '.geotiff', '.zip', '.rar'],
    struktur_data: ['.pdf', '.zip'], takel: ['.pdf', '.zip'], dokumen: ['.zip'],
    metadata: ['.xml', '.zip'], simbology: ['.zip'],
    role: ['.pdf', '.zip'], kualitas: ['.pdf', '.zip']
  };
  const toggleEditMode = () => {
    if (!editMode) {
      setEditMode(true);
      fetchLatestDocuments(); // Fetch latest documents when entering edit mode
      setTimeout(() => {
        document.querySelector('.detail-table-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      setEditMode(false);
      setDataDetail(originalData); // Kembalikan data ke state awal
      setFileData({}); // Kosongkan file yang siap diupload
      setFileValidationErrors({}); // Hapus error validasi
      setSyncedDocuments({}); // Clear synced documents
      setFileNames({}); // Clear file names
      toast.info('Mode edit ditutup, perubahan dibatalkan.');
    }
  };

  // Fetch latest integrated documents for synchronization
  const fetchLatestDocuments = async () => {
    if (!dataDetail?.nama_igt || !dataDetail?.user_id) return;
    
    try {
      const token = localStorage.getItem('token');
      const documentTypes = ['struktur_data', 'takel', 'simbology', 'role'];
      const latestDocsData = {};
      
      for (const docType of documentTypes) {
        try {
          const response = await axios.get(
            `${config.API_URL}/data/latest-document`,
            {
              params: {
                nama_igt: dataDetail.nama_igt,
                userId: dataDetail.user_id,
                document_type: docType
              },
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          
          if (response.data.success && response.data.document) {
            latestDocsData[docType] = response.data.document;
          }
        } catch (error) {
          // Silent fail for individual document types
          console.log(`No latest document found for ${docType}`);
        }
      }
      
      setLatestDocuments(latestDocsData);
    } catch (error) {
      console.error("Error fetching latest documents:", error);
    }
  };

  // Handle synchronize document
  const handleSyncDocument = async (documentType) => {
    const latestDoc = latestDocuments[documentType];
    if (!latestDoc) return;
    
    // Set synced document
    setSyncedDocuments(prev => ({ ...prev, [documentType]: latestDoc }));
    
    // Clear any uploaded file for this type
    setFileData(prev => {
      const newFiles = { ...prev };
      delete newFiles[documentType];
      return newFiles;
    });
    
    // Set filename display
    setFileNames(prev => ({ 
      ...prev, 
      [documentType]: `🔄 ${latestDoc.filename} (tersinkronisasi dari ${latestDoc.no_tiket})` 
    }));
    
    // Fetch and sync checklist data if available
    try {
      const token = localStorage.getItem('token');
      
      // First check if checklist exists for this document type
      const checklistResponse = await axios.get(
        `${config.API_URL}/api/checklist/${documentType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Only proceed if checklist items exist
      if (checklistResponse.data && checklistResponse.data.checklist && checklistResponse.data.checklist.length > 0) {
        // Now fetch the checklist data
        const dataResponse = await axios.get(
          `${config.API_URL}/api/checklist/data/${latestDoc.no_tiket}/${documentType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (dataResponse.data && dataResponse.data.checklistData && dataResponse.data.checklistData.items) {
          const syncedItems = dataResponse.data.checklistData.items;
          
          // Merge synced checklist data with existing user inputs
          setChecklistInputs(prev => {
            const newInputs = { ...prev };
            Object.keys(syncedItems).forEach(itemId => {
              if (syncedItems[itemId].userInput) {
                newInputs[itemId] = syncedItems[itemId].userInput;
              }
            });
            return newInputs;
          });
          
          console.log(`✅ Checklist data synced for ${documentType}`);
        }
      } else {
        console.log(`ℹ️ No checklist template found for ${documentType}`);
      }
    } catch (error) {
      // Silent fail - checklist data might not exist
      console.log(`ℹ️ No checklist data found for ${documentType}:`, error.message);
    }
    
    const docLabels = {
      struktur_data: 'Struktur Data',
      takel: 'Mekanisme dan Tata Kerja',
      simbology: 'Simbology',
      role: 'Role Akses'
    };
    
    toast.success(`${docLabels[documentType]} berhasil disinkronisasi dari tiket ${latestDoc.no_tiket}!`);
  };
  
  // Handle unsync document
  const handleUnsyncDocument = (documentType) => {
    setSyncedDocuments(prev => {
      const newSynced = { ...prev };
      delete newSynced[documentType];
      return newSynced;
    });
    
    setFileNames(prev => {
      const newNames = { ...prev };
      delete newNames[documentType];
      return newNames;
    });
    
    const docLabels = {
      struktur_data: 'Struktur Data',
      takel: 'Mekanisme dan Tata Kerja', 
      simbology: 'Simbology',
      role: 'Role Akses'
    };
    
    toast.info(`${docLabels[documentType]} tidak lagi tersinkronisasi.`);
  };

  // Handle opening Metadata Editor 
  const handleOpenMetadataEditor = async () => {
    if (!dataDetail || !dataDetail.metadata) {
      toast.warning('File metadata tidak tersedia');
      return;
    }
    
    // Check if file is XML
    const metadataUrl = dataDetail.metadata;
    if (!metadataUrl.toLowerCase().endsWith('.xml')) {
      toast.warning('Hanya file XML yang bisa dilihat di editor');
      return;
    }
    
    try {
      setMetadataUrl(metadataUrl);
      
      // Download via metadata API endpoint using noTiket parameter
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${config.API_URL}/metadata/download`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { noTiket: dataDetail.no_tiket },
          responseType: 'text'
        }
      );
      
      setMetadataContent(response.data);
      setShowMetadataEditor(true);
    } catch (error) {
      console.error('Error loading metadata:', error);
      toast.error('Gagal memuat file metadata');
    }
  };
  
  // Handle saving metadata from editor
  const handleSaveMetadata = async (xmlContent, values) => {
    try {
      // Create a Blob from XML string
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      const file = new File([blob], 'metadata_updated.xml', { type: 'application/xml' });
      
      // Update file data
      setFileData(prev => ({ ...prev, metadata: file }));
      
      // Close editor
      setShowMetadataEditor(false);
      setMetadataContent(null);
      
      toast.success('Metadata berhasil diperbarui! Jangan lupa klik "Unggah File Revisi" untuk menyimpan.');
    } catch (error) {
      console.error('Error saving metadata:', error);
      toast.error('Gagal menyimpan metadata');
    }
  };
  
  // Handle closing Metadata Editor
  const handleCloseMetadataEditor = () => {
    setShowMetadataEditor(false);
    setMetadataContent(null);
  };

  // Komponen Modal Konfirmasi untuk Upload Revisi
  const ConfirmationModal = ({ isOpen, onClose, onConfirm, loading }) => {
    if (!isOpen) return null;

    return (
      <div className="survey-modal-overlay">
        <div className="survey-modal" style={{ maxWidth: '450px' }}>
          <div className="survey-header">
            <div className="survey-header-content">
              <AlertTriangle size={24} className="survey-icon" style={{ color: '#f59e0b' }} />
              <div>
                <h3>Konfirmasi Unggah Revisi</h3>
                <p>Apakah Anda yakin ingin mengirimkan file revisi ini? Proses ini tidak dapat dibatalkan.</p>
              </div>
            </div>
          </div>
          <div className="form-actions" style={{ padding: '20px 30px', borderTop: 'none' }}>
            <button onClick={onClose} className="btn-cancel" disabled={loading}>
              Batal
            </button>
            <button onClick={onConfirm} className="btn-confirm" disabled={loading}>
              {loading ? 'Mengunggah...' : 'Ya, Kirim Revisi'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Fungsi untuk memvalidasi format file yang diunggah
  const validateFileFormat = (file, field) => {
    if (!file) return true;
    const fileName = file.name.toLowerCase();
    const allowedExtensions = allowedFormats[field] || [];
    const isValidFormat = allowedExtensions.some(ext => fileName.endsWith(ext.toLowerCase()));
    if (!isValidFormat) {
      const errorMessage = `Format file tidak valid. Hanya diizinkan: ${allowedExtensions.join(', ')}`;
      setFileValidationErrors(prev => ({ ...prev, [field]: errorMessage }));
      toast.error(errorMessage);
      return false;
    }
    setFileValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
    return true;
  };

  // Fungsi untuk membersihkan nama file
  const cleanFileName = (fileName) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    const name = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);
    const cleanedName = name
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');

    return cleanedName + extension;
  };

  // Fungsi untuk menangani perubahan pada input file
  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file && validateFileFormat(file, field)) {
      const cleanedFileName = cleanFileName(file.name);
      const cleanedFile = new File([file], cleanedFileName, {
        type: file.type,
        lastModified: file.lastModified,
      });
      setFileData({ ...fileData, [field]: cleanedFile });
    } else if (file) {
      e.target.value = '';
      setFileData({ ...fileData, [field]: null });
    }
  };

  // Fungsi untuk mengunduh dokumen catatan verifikator
  const downloadVerificationDoc = async (docIndex) => { // Tambahkan parameter docIndex
    try {
      const token = localStorage.getItem('token');
      const url = `${config.API_URL}/dataAdmin/downloadVerificationDoc/${btoa(dataDetail.no_tiket)}/${docIndex}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `Verifikasi_${dataDetail.no_tiket}_Catatan${docIndex}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error('Gagal mengunduh dokumen verifikasi.');
    }
  };

  const handleSaveConfirmation = () => {
    if (Object.keys(fileValidationErrors).length > 0) {
      toast.error('Mohon perbaiki format file yang tidak valid sebelum melanjutkan.');
      return;
    }
    setIsConfirmModalOpen(true);
  };

  // Fungsi untuk mengirim data revisi
  const handleSubmit = async (e) => {
    if (Object.keys(fileValidationErrors).length > 0) {
      toast.error('Mohon perbaiki format file yang tidak valid sebelum upload.');
      return;
    }

    const formData = new FormData();
    Object.keys(fileData).forEach(key => {
      if (fileData[key]) formData.append(key, fileData[key]);
    });
    
    // Add sync parameters for synced documents
    if (syncedDocuments.struktur_data) {
      formData.append('syncStruktur_dataTicket', syncedDocuments.struktur_data.no_tiket);
    }
    if (syncedDocuments.takel) {
      formData.append('syncTakelTicket', syncedDocuments.takel.no_tiket);
    }
    if (syncedDocuments.simbology) {
      formData.append('syncSimbolotyTicket', syncedDocuments.simbology.no_tiket);
    }
    if (syncedDocuments.role) {
      formData.append('syncRoleTicket', syncedDocuments.role.no_tiket);
    }
    
    formData.append('cakupan', dataDetail.cakupan);
    formData.append('status', 'Revisi Pengajuan'); // Status otomatis
    formData.append('checklistData', JSON.stringify(checklistInputs));
    formData.append('capaianData', JSON.stringify(capaianData));
    formData.append('futureYearsTarget', JSON.stringify(futureYearsTarget));

    setLoading(true);
    setIsConfirmModalOpen(true);
    try {
      const token = localStorage.getItem('token');
      const decoded = jwtDecode(token);
      formData.append('userId', decoded.userId);
      const response = await axios.post(`${config.API_URL}/data/upload/${atob(no_tiket)}`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      setEditMode(false);
      setDataDetail(response.data);
      setStatus('Revisi Pengajuan');
      toast.success('Data berhasil diupdate! Status telah berubah menjadi Revisi Pengajuan.');
      navigate(`/kl/success?no_tiket=${atob(no_tiket)}`);
    } catch (err) {
      toast.error('Gagal memperbaharui file.');
    } finally {
      setLoading(false);
      setUploadProgress(0);
      setIsConfirmModalOpen(false);
    }
  };

  // Update capaian data
  const updateCapaian = (index, field, value) => {
    const newCapaian = [...capaianData];
    newCapaian[index][field] = value;
    
    // Recalculate persentase if nilai or target changed
    if (field === 'nilai' || field === 'target') {
      const nilai = parseFloat(newCapaian[index].nilai) || 0;
      const target = parseFloat(newCapaian[index].target) || 0;
      if (target > 0) {
        const calculated = (nilai / target) * 100;
        newCapaian[index].persentase = Math.min(100, calculated).toFixed(2);
      } else {
        newCapaian[index].persentase = 0;
      }
    }
    
    setCapaianData(newCapaian);
  };

  // Update future years target
  const updateFutureYear = (index, field, value) => {
    const newFutureYears = [...futureYearsTarget];
    newFutureYears[index][field] = value;
    setFutureYearsTarget(newFutureYears);
  };

  // Fungsi pembantu (helpers)
  const toggleVisibility = () => window.pageYOffset > 300 ? setIsVisible(true) : setIsVisible(false);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const isDocumentApproved = (field) => catatan?.[field]?.split('|')[1] === 'Sesuai';
  const isInVerificationProcess = () => status === 'Sedang Verifikasi';
  const isInRevisionProcess = () => status === 'Revisi Pengajuan';
  const shouldShowVerifierNotes = () => {
    return status === 'Perlu Perbaikan' || status === 'Sudah Terintegrasi';
  };
  const getStatusClass = (status) => {
    if (status === 'Sesuai') return 'status-approved';
    if (status === 'Tidak Sesuai') return 'status-rejected';
    return 'status-pending';
  };
  const getStatusIcon = (status) => {
    if (status === 'Sesuai') return <CheckCircle size={16} className="status-icon-approved" />;
    if (status === 'Tidak Sesuai') return <XCircle size={16} className="status-icon-rejected" />;
    return null;
  };

  // Axios interceptor untuk menangani session expired
  /*   axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          navigate('/', { state: { sessionExpired: true } });
        }
        return Promise.reject(error);
      }
    ); */

  // Fetch checklist items
  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${config.API_URL}/api/checklist/all`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.data.success) {
          const activeChecklist = response.data.checklist.filter(item => item.is_active);
          setChecklistData(activeChecklist);
        }
      } catch (error) {
        console.error("Error fetching checklist:", error);
      }
    };
    fetchChecklist();
  }, []);

  // Fetch data saat komponen dimuat
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const decoded = jwtDecode(token);
        if (!token || !decoded.twoFactorVerified) {
          navigate('/');
          return;
        }
        const response = await axios.get(`${config.API_URL}/data/detail/${atob(no_tiket)}?username=${decoded.username}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        setDataDetail(response.data);
        setOriginalData(response.data); // Simpan data original untuk reset
        setStatus(response.data.status);
        setCatatan(response.data.catatan || {});
        checkChecklistData(response.data.no_tiket);
        setFilerekom(response.data.rekom || '');
        setFileba(response.data.batte || '');
        
        // Load existing checklist data
        if (response.data.checklist_data) {
          setChecklistInputs(response.data.checklist_data);
        }
        
        // Load capaian data
        if (response.data.capaian_data) {
          try {
            const parsedCapaian = typeof response.data.capaian_data === 'string' 
              ? JSON.parse(response.data.capaian_data) 
              : response.data.capaian_data;
            setCapaianData(parsedCapaian);
          } catch (error) {
            console.error('Error parsing capaian_data:', error);
            setCapaianData([]);
          }
        }
        
        // Load kepka info if available
        if (response.data.kepka_id) {
          try {
            const kepkaResponse = await axios.get(`${config.API_URL}/kepka/${response.data.kepka_id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            setKepkaInfo(kepkaResponse.data);
            
            // Extract future years from kepka target
            if (kepkaResponse.data.target) {
              const currentYear = new Date().getFullYear();
              const futureYears = Object.entries(kepkaResponse.data.target)
                .filter(([year]) => parseInt(year) > currentYear)
                .map(([year, target]) => ({
                  tahun: parseInt(year),
                  target: target.toString(),
                  satuan: kepkaResponse.data.satuan || ''
                }))
                .sort((a, b) => a.tahun - b.tahun);
              setFutureYearsTarget(futureYears);
            }
          } catch (error) {
            console.error('Error fetching kepka info:', error);
            setKepkaInfo(null);
          }
        }
        // Handle multiple verification documents with sorting
        if (response.data.verification_notes && Array.isArray(response.data.verification_notes)) {
          const sortedDocs = response.data.verification_notes.sort((a, b) => a.index - b.index);
          setVerificationDocs(sortedDocs);
        } else if (response.data.verification_note) {
          setVerificationDocs([{
            index: 1,
            name: 'Catatan Verifikator',
            url: response.data.verification_note
          }]);
        } else {
          setVerificationDocs([]);
        }
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setError('Data dengan tiket ini sudah tidak ada atau telah dihapus.');
        } else {
          setError('Gagal memuat detail data. Silakan coba lagi.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, [no_tiket, navigate]);


  if (loading) {
    return <div className="loading-container"><div className="loading-spinner"></div><p>Memuat data...</p></div>;
  }
  if (error) {
    return <div className="error-container"><div className="alert alert-danger">{error}</div></div>;
  }

  return (
    <DashboardLayout 
      menuItems={getKLMenuItems()}
      userRole="KL"
    >
      <ToastContainer position="top-center" autoClose={4000} theme="colored" />
      <div className="container mt-4">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/kl">Dashboard</a></li>
            <li className="breadcrumb-item"><a href="/kl/data">Daftar Data</a></li>
            <li className="breadcrumb-item active" aria-current="page">
              Detail Data - {dataDetail?.no_tiket || ''}
            </li>
          </ol>
        </nav>
        
        {dataDetail && (
          <div className="rekap-detail-wrapper">
            {/* Header Card */}
            <div className="rekap-header-card">
              <button onClick={() => navigate('/kl/data')} className="close-button"><X size={20} /></button>
              <div className="header-content">
                <h2 className="rekap-title">Detail Pengajuan Data</h2>
                <div className="ticket-info">
                  <span className="ticket-label">No. Tiket:</span>
                  <span className="ticket-number">{dataDetail.no_tiket}</span>
                </div>
              </div>
            </div>

            {/* Status Summary Card */}
            <div className="status-summary-card">
              <div className="summary-grid">
                <div className="summary-item"><label>Nama IGT</label><span>{dataDetail.nama_igt}</span></div>
                <div className="summary-item"><label>Jenis IGT</label><span>{dataDetail.jenis_igt}</span></div>
                <div className="summary-item"><label>KL</label><span>{dataDetail.kl}</span></div>
                <div className="summary-item">
                  <label>Status Pengajuan</label>
                  <span className={`status-badge status-${(dataDetail.status || "").toLowerCase().replace(/ /g, '-')}`}>
                    {dataDetail.status}
                  </span>
                </div>
                {dataDetail.jenis_igt === 'Renaksi PKSP' && (
                  <div className="summary-item">
                    <label>Status Publikasi</label>
                    {dataDetail.status_publikasi ? (
                      <span className={`status-badge status-${dataDetail.status_publikasi.toLowerCase().replace(/ /g, '-')}`}>
                        {dataDetail.status_publikasi}
                      </span>
                    ) : (
                      <span className="status-badge" style={{ backgroundColor: '#6c757d', color: '#fff' }}>-</span>
                    )}
                  </div>
                )}
              </div>
            </div>


            {/* Dokumen Hasil Verifikasi (ditampilkan jika status BUKAN 'Sedang Verifikasi' atau 'Revisi Pengajuan') */}
            {!isInVerificationProcess() && !isInRevisionProcess() && (filerekom || fileba) && (
              <div className="files-section">
                <h3 className="section-title">Dokumen Hasil Verifikasi</h3>
                <div className="files-grid">
                  {filerekom && (
                    <div className="file-card">
                      <div className="file-icon"><Download size={24} /></div>
                      <div className="file-info">
                        <h4>Rekomendasi Hasil Verifikasi</h4>
                        <a
                          href={`${config.API_URL}/downloadsurat/${btoa(filerekom.split('/').pop())}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="file-download-link"
                        >
                          Unduh Dokumen
                        </a>
                      </div>
                    </div>
                  )}

                  {fileba && (
                    <div className="file-card">
                      <div className="file-icon"><Download size={24} /></div>
                      <div className="file-info">
                        <h4>Rekomendasi Integrasi</h4>
                        <a
                          href={`${config.API_URL}/downloadsurat/${btoa(fileba.split('/').pop())}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="file-download-link"
                        >
                          Unduh Dokumen
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Section terpisah untuk Dokumen Catatan Verifikator */}
            {shouldShowVerifierNotes() && verificationDocs.length > 0 && (
              <div className="files-section">
                <h3 className="section-title">
                  <MessageSquare size={18} style={{ marginRight: '8px' }} />
                  Dokumen Catatan Verifikator : {dataDetail.fullname || ''}
                </h3>
                <div className="files-grid">
                  {verificationDocs.map((doc, i) => (
                    <div key={doc.index} className="file-card">
                      <div className="file-icon"><Download size={24} /></div>
                      <div className="file-info">
                        <h4>{`Catatan ${i + 1}`}</h4>
                        <button
                          onClick={() => downloadVerificationDoc(doc.index)}
                          className="file-download-link"
                        >
                          Unduh Dokumen
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="action-button-container">
              {status === "Perlu Perbaikan" && !isInRevisionProcess() && (
                <button className="edits-button" onClick={toggleEditMode}>
                  <Edit size={16} />
                  {editMode ? 'Tutup Mode Edit' : 'Edit Kelengkapan'}
                </button>
              )}
            </div>
            {/* Detail Table Section */}
            <div className="detail-table-section">
              <h3 className="section-title">Detail Kelengkapan Dokumen</h3>

              {editMode && <div className="alert-custom alert-custom-info"><Edit size={18} /><div><strong>Mode Edit Aktif:</strong> Anda dapat mengunggah file revisi untuk item yang bertanda 'Tidak Sesuai'.</div></div>}
              {isInRevisionProcess() && <div className="alert-custom alert-custom-info"><Info size={18} /><strong>Data dalam Proses Revisi:</strong> Pengajuan Anda sedang ditinjau ulang.</div>}
              {isInVerificationProcess() && <div className="alert-custom alert-custom-warning"><Hourglass size={18} /><strong>Data dalam Proses Verifikasi:</strong> Anda hanya dapat melihat file yang telah diunggah.</div>}
              {Object.keys(fileValidationErrors).length > 0 && <div className="alert-custom alert-custom-danger"><AlertTriangle size={18} />Format file tidak sesuai. Mohon perbaiki sebelum mengunggah.</div>}

              <div className="table-wrapper">
                <div className="table-container">
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th className="col-document">Dokumen</th>
                        <th className="col-file">File</th>
                        {!isInVerificationProcess() && <><th className="col-notes">Catatan</th><th className="col-status">Status</th></>}
                      </tr>
                    </thead>
                    <tbody>
                      {documentItems.map((item) => {
                        const documentStatus = catatan?.[item.key]?.split('|')[1];
                        const notes = catatan?.[item.key]?.split('|')[0] || '';
                        const isApproved = isDocumentApproved(item.key);

                        const documentChecklist = checklistData.filter(cl => cl.document_type === item.key);
                        const hasChecklist = documentChecklist.length > 0;
                        
                        return (
                          <React.Fragment key={item.key}>
                          <tr>
                            <td className="document-cell"><div className="document-info">{item.icon}<span className="dok-name">{item.label}</span></div></td>
                            <td className="file-cell">
                              {editMode && !isApproved ? (
                                <div>
                                  {/* Sync Section for Syncable Documents */}
                                  {['struktur_data', 'takel', 'simbology', 'role'].includes(item.key) && latestDocuments[item.key] && (
                                    <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <label style={{ fontSize: '13px', fontWeight: '500', margin: 0, color: '#15803d' }}>
                                          {syncedDocuments[item.key] ? '🔄' : '⚡'} Pakai data {item.label} yang sudah terintegrasi ?
                                        </label>
                                      </div>
                                      <small style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '8px' }}>
                                        {syncedDocuments[item.key] 
                                          ? `Tersinkronisasi dari nomer tiket ${syncedDocuments[item.key].no_tiket}`
                                          : `Data sebelumnya Sudah Terintegrasi dengan nama dokumen : ${latestDocuments[item.key].filename}`
                                        }
                                      </small>
                                      <div style={{ display: 'flex', gap: '8px' }}>
                                        {syncedDocuments[item.key] ? (
                                          <button
                                            type="button"
                                            onClick={() => handleUnsyncDocument(item.key)}
                                            style={{
                                              padding: '6px 12px',
                                              backgroundColor: '#ef4444',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '6px',
                                              fontSize: '12px',
                                              fontWeight: '500',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            Batalkan Sinkronisasi
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={() => handleSyncDocument(item.key)}
                                            style={{
                                              padding: '6px 12px',
                                              backgroundColor: '#10b981',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '6px',
                                              fontSize: '12px',
                                              fontWeight: '500',
                                              cursor: 'pointer'
                                            }}
                                          >
                                            Gunakan Data Ini
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* File Input - Disabled if synced */}
                                  <input 
                                    className="form-control" 
                                    type="file" 
                                    onChange={(e) => handleFileChange(e, item.key)} 
                                    accept={allowedFormats[item.key]?.join(',')} 
                                    disabled={!!syncedDocuments[item.key]}
                                    style={syncedDocuments[item.key] ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                                  />
                                  
                                  {/* Tombol Edit Metadata di mode edit */}
                                  {item.key === 'metadata' && dataDetail.metadata && dataDetail.metadata.toLowerCase().endsWith('.xml') && (
                                    <button
                                      onClick={handleOpenMetadataEditor}
                                      className="file-download-btn"
                                      style={{ 
                                        marginTop: '8px',
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                      }}
                                    >
                                      <Edit3 size={14} />
                                      <span>Edit Metadata</span>
                                    </button>
                                  )}
                                  
                                  {/* Show synced filename */}
                                  {fileNames[item.key] && (
                                    <div style={{ marginTop: '8px', fontSize: '13px', color: '#059669', fontWeight: '500' }}>
                                      {fileNames[item.key]}
                                    </div>
                                  )}
                                  
                                  {fileValidationErrors[item.key] && <div className="text-danger mt-1 small">{fileValidationErrors[item.key]}</div>}
                                </div>
                              ) : (
                                dataDetail[item.key] ? (
                                  <div className="file-download-container">
                                    {item.key === 'peta' && dataDetail.status_enkripsi === 'sealed' ? (
                                      <button
                                        onClick={() => openUnsealModal({ noTiket: dataDetail.no_tiket, namaIgt: dataDetail.nama_igt })}
                                        className="file-download-btn"
                                      >
                                        <Download size={14} />
                                        <span>Unduh File {item.label}</span>
                                      </button>
                                    ) : item.key === 'metadata' ? (
                                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <a
                                          href={`${config.API_URL}/download/${btoa(dataDetail[item.key].split('/').pop())}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="file-download-btn"
                                        >
                                          <FileText size={16} />
                                          <span>Unduh File</span>
                                          <Download size={14} />
                                        </a>
                                        {dataDetail[item.key].toLowerCase().endsWith('.xml') && (
                                          <button
                                            onClick={handleOpenMetadataEditor}
                                            className="file-download-btn"
                                            style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}
                                          >
                                            <Edit3 size={14} />
                                            <span>Lihat Metadata</span>
                                          </button>
                                        )}
                                      </div>
                                    ) : (
                                      <a
                                        href={`${config.API_URL}/download/${btoa(dataDetail[item.key].split('/').pop())}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="file-download-btn"
                                      >
                                        <FileText size={16} />
                                        <span>Unduh file {item.label}</span>
                                        <Download size={14} />
                                      </a>
                                    )}
                                  </div>
                                ) : (
                                  <div className="no-file-indicator">
                                    <span>File tidak tersedia</span>
                                  </div>
                                )
                              )}
                            </td>
                            {!isInVerificationProcess() && (
                              <>
                                <td className="notes-cell"><div className="notes-content"><p>{notes || "-"}</p></div></td>
                                <td className="status-cell">
                                  <div className="status-with-checklist">
                                    {documentStatus && <div className={`status-badge ${getStatusClass(documentStatus)}`}>{getStatusIcon(documentStatus)}<span>{documentStatus}</span></div>}
                                    {checklistStatus[item.key] && (
                                      <button
                                        onClick={() => handleChecklistViewOpen(item.key)}
                                        className="checklist-view-btn-icon"
                                        title="Lihat Hasil Checklist"
                                      >
                                        <ClipboardCheck size={14} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                          
                          {/* Checklist Row - Only in Edit Mode */}
                          {editMode && hasChecklist && (
                            <tr>
                              <td colSpan={!isInVerificationProcess() ? 4 : 2} style={{ padding: '0', border: 'none' }}>
                                <div style={{ 
                                  marginTop: '8px',
                                  marginBottom: '12px',
                                  padding: '0',
                                  border: '2px dashed #cbd5e1',
                                  borderRadius: '8px'
                                }}>
                                  <div 
                                    onClick={() => setExpandedChecklists(prev => ({
                                      ...prev,
                                      [item.key]: !prev[item.key]
                                    }))}
                                    style={{ 
                                      cursor: 'pointer', 
                                      display: 'flex', 
                                      alignItems: 'center',
                                      padding: '12px 16px',
                                      background: '#f8fafc',
                                      borderBottom: expandedChecklists[item.key] ? '2px dashed #cbd5e1' : 'none',
                                      transition: 'background 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = '#f8fafc'}
                                  >
                                    <span style={{ fontWeight: '600', color: '#334155', fontSize: '14px', flex: 1 }}>
                                      Informasi Tambahan ({documentChecklist.length} item)
                                    </span>
                                    <span style={{ color: '#64748b', fontSize: '14px' }}>
                                      {expandedChecklists[item.key] ? '▲' : '▼'}
                                    </span>
                                  </div>
                                  
                                  {expandedChecklists[item.key] && (
                                    <div style={{ padding: '16px' }}>
                                      {documentChecklist.map((clItem, idx) => (
                                        <div key={clItem.id} style={{ 
                                          display: 'grid', 
                                          gridTemplateColumns: '2fr 3fr 2fr', 
                                          gap: '12px', 
                                          alignItems: 'center',
                                          marginBottom: idx < documentChecklist.length - 1 ? '12px' : '0',
                                          paddingBottom: idx < documentChecklist.length - 1 ? '12px' : '0',
                                          borderBottom: idx < documentChecklist.length - 1 ? '1px solid #e2e8f0' : 'none'
                                        }}>
                                          <div style={{ fontSize: '13px', fontWeight: '500', color: '#475569' }}>
                                            {clItem.description}
                                          </div>
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={checklistInputs[clItem.id] || ''}
                                            onChange={(e) => setChecklistInputs(prev => ({
                                              ...prev,
                                              [clItem.id]: e.target.value
                                            }))}
                                            placeholder={clItem.input_hint || 'Masukkan informasi...'}
                                            style={{ fontSize: '13px', padding: '8px 12px' }}
                                          />
                                          {clItem.input_hint ? (
                                            <small style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                                              {clItem.input_hint}
                                            </small>
                                          ) : (
                                            <small style={{ fontSize: '12px', color: '#94a3b8' }}>-</small>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                      {/* Baris untuk Cakupan */}
                      <tr>
                        <td className="document-cell"><div className="document-info"><Globe className="document-lucide-icon" /><span className="dok-name">Cakupan</span></div></td>
                        <td className="file-cell">
                          {editMode ? <textarea className="form-control" value={dataDetail.cakupan} onChange={(e) => setDataDetail({ ...dataDetail, cakupan: e.target.value })} rows="3" /> : <div className="cakupan-text-inline">{dataDetail.cakupan || "-"}</div>}
                        </td>
                        {!isInVerificationProcess() && (
                          <>
                            <td className="notes-cell"><div className="notes-content"><p>{catatan?.cakupan?.split('|')[0] || '-'}</p></div></td>
                            <td className="status-cell">
                              <div className="status-with-checklist">
                                {catatan?.cakupan?.split('|')[1] && <div className={`status-badge ${getStatusClass(catatan?.cakupan?.split('|')[1])}`}>{getStatusIcon(catatan?.cakupan?.split('|')[1])}<span>{catatan?.cakupan?.split('|')[1]}</span></div>}
                                {checklistStatus['cakupan'] && (
                                  <button
                                    onClick={() => handleChecklistViewOpen('cakupan')}
                                    className="checklist-view-btn-icon"
                                    title="Lihat Hasil Checklist"
                                  >
                                    <ClipboardCheck size={14} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Data Capaian Section */}
              {capaianData && capaianData.length > 0 && (
                <div className="files-section" style={{ marginTop: '30px' }}>
                  <h3 className="section-title">
                    <FileText className="me-2" size={22} />
                    Data Capaian
                  </h3>
                  
                  {/* Kepka Info */}
                  {kepkaInfo && (
                    <div style={{
                      padding: '12px 16px',
                      backgroundColor: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '14px' }}>
                        <span><strong>Jenis SK:</strong> {kepkaInfo.jenis}</span>
                        <span><strong>Nama IGT:</strong> {kepkaInfo.nama_igt_walidata}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Capaian Table */}
                  <div className="table-wrapper">
                    <div className="table-container">
                      <table className="detail-table">
                        <thead>
                          <tr>
                            <th style={{ width: '100px', textAlign: 'center' }}>Tahun</th>
                            <th style={{ width: '150px', textAlign: 'center' }}>Capaian</th>
                            <th style={{ width: '150px', textAlign: 'center' }}>Target</th>
                            <th style={{ width: '120px', textAlign: 'center' }}>Satuan</th>
                            <th style={{ width: '120px', textAlign: 'center' }}>Persentase</th>
                          </tr>
                        </thead>
                        <tbody>
                          {capaianData.map((item, index) => (
                            <tr key={index}>
                              <td style={{ textAlign: 'center', fontWeight: '600' }}>{item.tahun}</td>
                              <td>
                                {editMode ? (
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={item.nilai || ''}
                                    onChange={(e) => updateCapaian(index, 'nilai', e.target.value)}
                                    min="0"
                                    step="0.01"
                                  />
                                ) : (
                                  <div style={{ textAlign: 'center' }}>{item.nilai || '-'}</div>
                                )}
                              </td>
                              <td style={{ textAlign: 'center' }}>{item.target || '-'}</td>
                              <td style={{ textAlign: 'center' }}>{item.satuan || '-'}</td>
                              <td style={{ textAlign: 'center' }}>
                                <span 
                                  className="badge" 
                                  style={{
                                    backgroundColor: parseFloat(item.persentase) >= 50 ? '#10b981' : '#f59e0b',
                                    color: 'white',
                                    padding: '6px 12px',
                                    fontSize: '13px',
                                    fontWeight: '600'
                                  }}
                                >
                                  {item.persentase || 0}%
                                </span>
                              </td>
                            </tr>
                          ))}
                          
                          {/* Future Years Target (editable if SK Walidata) */}
                          {futureYearsTarget.map((item, index) => (
                            <tr key={`future-${index}`} style={{ backgroundColor: '#f9fafb' }}>
                              <td style={{ textAlign: 'center', fontWeight: '600', color: '#6b7280' }}>{item.tahun}</td>
                              <td style={{ textAlign: 'center', color: '#9ca3af' }}>-</td>
                              <td>
                                {editMode && kepkaInfo?.jenis === 'SK Walidata' ? (
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={item.target || ''}
                                    onChange={(e) => updateFutureYear(index, 'target', e.target.value)}
                                    min="0"
                                    step="0.01"
                                  />
                                ) : (
                                  <div style={{ textAlign: 'center' }}>{item.target || '-'}</div>
                                )}
                              </td>
                              <td style={{ textAlign: 'center' }}>{item.satuan || '-'}</td>
                              <td style={{ textAlign: 'center' }}>
                                <span 
                                  className="badge" 
                                  style={{
                                    backgroundColor: '#e5e7eb',
                                    color: '#6b7280',
                                    padding: '6px 12px',
                                    fontSize: '13px',
                                    fontWeight: '600'
                                  }}
                                >
                                  Target
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {editMode && (
                <div className="mt-4 text-center">
                  {uploadProgress > 0 && <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated striped className="mb-3" />}
                  <button onClick={handleSaveConfirmation} className="btn-submit" disabled={loading || Object.keys(fileValidationErrors).length > 0}>
                    {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <Download size={16} />}
                    {loading ? 'Memproses...' : 'Unggah File Revisi'}
                  </button>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
      <UnsealProgressModal
        isOpen={isUnsealModalOpen}
        onClose={closeUnsealModal}
        onComplete={handleUnsealComplete}
        noTiket={selectedTicket.noTiket}
        namaIgt={selectedTicket.namaIgt}
        mode="unseal"
      />
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleSubmit}
        loading={loading}
      />
      {isVisible && <button onClick={scrollToTop} className="scroll-to-top"><ArrowUp size={24} /></button>}
      
      {/* Metadata Editor Modal - Only editable in edit mode */}
      {showMetadataEditor && (
        <MetadataEditor
          isOpen={showMetadataEditor}
          onClose={handleCloseMetadataEditor}
          onSave={handleSaveMetadata}
          metadataFile={metadataContent}
          readOnly={!editMode}
        />
      )}
      
      <DataChecklistView
        isOpen={isChecklistViewOpen}
        onClose={handleChecklistViewClose}
        documentType={selectedDocumentType}
        noTiket={dataDetail?.no_tiket}
      />
    </DashboardLayout>
  );
};

export default DataDetailPage;